import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { userApi } from '../api';

export interface User {
  name: string;
  email: string;
  profilePic: string;
  role: string;
  phone?: string;
  dob?: string;
  state?: string;
  city?: string;
}

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  login: (userData: User, token: string) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  fetchAndUpdateUser: (token?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  const fetchAndUpdateUser = useCallback(async (token?: string) => {
    const userToken = token || localStorage.getItem('zoniq_token');
    if (!userToken) {
      logout();
      return;
    }
    try {
      const [account, profile] = await Promise.all([
        userApi.getAccount(userToken),
        userApi.getProfile(userToken).catch(() => null)
      ]);
      
      const savedUser = localStorage.getItem('zoniq_user');
      const parsedSavedUser = savedUser ? JSON.parse(savedUser) : {};

      const profileHasPicField = Boolean(
        profile &&
          (Object.prototype.hasOwnProperty.call(profile, 'profilePicUrl') ||
            Object.prototype.hasOwnProperty.call(profile, 'profile_pic_url') ||
            Object.prototype.hasOwnProperty.call(profile, 'profilePic'))
      );

      const finalUser: User = {
        ...parsedSavedUser,
        ...(account || {}),
        ...(profile || {}),
        name: profile?.fullName || profile?.full_name || account?.fullName || account?.name || parsedSavedUser.name,
        email: account?.email || parsedSavedUser.email,
        profilePic: profileHasPicField
          ? (profile?.profilePicUrl ?? profile?.profile_pic_url ?? profile?.profilePic ?? '')
          : (parsedSavedUser.profilePic || ''),
        role: account?.role || parsedSavedUser.role,
        dob: profile?.dateOfBirth || profile?.date_of_birth || profile?.dob || parsedSavedUser.dob,
        phone: profile?.phone ?? account?.phone ?? parsedSavedUser.phone,
        state: profile?.state || parsedSavedUser.state,
        city: profile?.city || parsedSavedUser.city
      };

      setUser(finalUser);
      localStorage.setItem('zoniq_user', JSON.stringify(finalUser));
      setIsLoggedIn(true);
    } catch (error) {
      console.error("Failed to fetch user profile", error);
      logout();
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('zoniq_token');
    if (token) {
      fetchAndUpdateUser(token);
    }
  }, [fetchAndUpdateUser]);

  const login = (userData: User, token: string) => {
    localStorage.setItem('zoniq_auth', 'true');
    localStorage.setItem('zoniq_token', token);
    localStorage.setItem('zoniq_user', JSON.stringify(userData));
    setIsLoggedIn(true);
    setUser(userData);
    fetchAndUpdateUser(token);
  };

  const logout = () => {
    localStorage.removeItem('zoniq_auth');
    localStorage.removeItem('zoniq_token');
    localStorage.removeItem('zoniq_user');
    localStorage.removeItem('zoniq_cart');
    localStorage.removeItem('zoniq_orders');
    setIsLoggedIn(false);
    setUser(null);
  };

  const updateUser = (userData: Partial<User>) => {
    setUser(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...userData };
      localStorage.setItem('zoniq_user', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, login, logout, updateUser, fetchAndUpdateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
